import type { Loan, Trigger, TriggerSeverity, TriggerStatus } from '@/types'
import type { Notification } from '@/store/notificationStore'
import { deskDateIso } from '@/pages/pipeline-intelligence/deskTime'

/** A trigger as `GET /triggers/` returns it, after `toCamelCase` — whose key
 *  map renames `trigger_type` to `type` and `due_date` to `due`. */
export interface TriggerRow {
  id: string
  loanId: string
  type: string
  due: string | null
  status: string
  severity?: TriggerSeverity | null
  description?: string | null
}

export interface TriggerAlert {
  id: string
  severity: TriggerSeverity
  text: string
  /** Due date as stored (YYYY-MM-DD), or '—'. */
  due: string
  deal: string
  property: string
  overdue: boolean
}

const SEVERITY_RANK: Record<TriggerSeverity, number> = { high: 0, medium: 1, low: 2 }

/**
 * Today as YYYY-MM-DD on the desk's calendar (New York), the way due dates are written.
 *
 * The desk's day, not the reader's: the server decides "overdue" on the desk's calendar
 * (`app.underwriting.trigger_status.desk_today`), so a laptop in another time zone must not
 * list a requirement as overdue hours before — or after — the saved view that counts it does.
 */
export function deskToday(now: Date = new Date()): string {
  return deskDateIso(now.toISOString()) ?? ''
}

/** Overdue is a fact about the due date, not only the stored status: a trigger
 *  still marked "pending" the day after it fell due is overdue. */
export function isTriggerOverdue(trigger: TriggerRow, today: string): boolean {
  if (trigger.status === 'resolved') return false
  if (trigger.status === 'overdue') return true
  const due = (trigger.due ?? '').slice(0, 10)
  return /^\d{4}-\d{2}-\d{2}$/.test(due) && due < today
}

/**
 * Open triggers on the live book, as alerts.
 *
 * Command Center, its Alert Feed and the notification bell used to read
 * `loan.triggers`, which `GET /loans/` never returns — so they showed 0 and
 * "No active alerts" while an overdue insurance trigger sat on a deal (stress
 * test EXEC-06/EXEC-12). The triggers come from `/triggers/`; only those on a
 * deal in the loaded live book are kept, so a dead, archived or other-partition
 * deal never raises an alert here.
 */
export function deriveTriggerAlerts(
  triggers: TriggerRow[],
  loans: Loan[],
  today: string = deskToday(),
): TriggerAlert[] {
  const byId = new Map(loans.map((loan) => [loan.id, loan]))
  const alerts: TriggerAlert[] = []
  for (const trigger of triggers) {
    if (!trigger || trigger.status === 'resolved') continue
    const loan = byId.get(trigger.loanId)
    if (!loan) continue
    const label = trigger.description?.trim() || trigger.type
    alerts.push({
      id: trigger.id,
      severity: trigger.severity ?? 'medium',
      text: `${label} — ${loan.property}`,
      due: trigger.due?.slice(0, 10) || '—',
      deal: loan.id,
      property: loan.property,
      overdue: isTriggerOverdue(trigger, today),
    })
  }
  return alerts.sort(
    (a, b) =>
      Number(b.overdue) - Number(a.overdue) ||
      SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity] ||
      a.due.localeCompare(b.due),
  )
}

/**
 * The loaded live book with each deal's triggers from `/triggers/` on it.
 *
 * `GET /loans/` never carries `loan.triggers`, so every rule that reads them —
 * the Pipeline "Overdue requirement" exception and the Inspector's open
 * triggers — saw none while an overdue insurance trigger sat on Calloway Yards
 * (stress test EXEC-06, verifier 26 Sep). Status is normalised the way the
 * alert feed reads it: a trigger past its due date and not resolved is overdue.
 */
export function attachLiveTriggers(
  loans: Loan[],
  triggers: TriggerRow[],
  today: string = deskToday(),
): Loan[] {
  const byLoan = new Map<string, Trigger[]>()
  for (const row of triggers) {
    if (!row?.loanId) continue
    const status: TriggerStatus =
      row.status === 'resolved'
        ? 'resolved'
        : isTriggerOverdue(row, today)
          ? 'overdue'
          : row.status === 'upcoming'
            ? 'upcoming'
            : 'pending'
    const list = byLoan.get(row.loanId) ?? []
    list.push({
      id: row.id,
      type: row.type,
      due: row.due?.slice(0, 10) ?? '',
      status,
      severity: row.severity ?? 'medium',
      description: row.description ?? undefined,
    })
    byLoan.set(row.loanId, list)
  }
  return loans.map((loan) => ({ ...loan, triggers: byLoan.get(loan.id) ?? [] }))
}

const PRIORITY: Record<TriggerAlert['severity'], Notification['priority']> = {
  high: 'high',
  medium: 'medium',
  low: 'low',
}

/** One open trigger as a bell notification. An overdue trigger is stamped
 *  with its due date so the panel's Today / Earlier buckets read as the desk's
 *  calendar; a trigger not yet due is stamped now, never in the future. */
export function triggerNotification(alert: TriggerAlert, now: Date = new Date()): Notification {
  const dueAt = /^\d{4}-\d{2}-\d{2}$/.test(alert.due) ? new Date(`${alert.due}T12:00:00`) : now
  const due = dueAt.getTime() < now.getTime() ? dueAt : now
  return {
    id: `live-trigger-${alert.id}`,
    type: 'trigger',
    title: alert.overdue ? 'Trigger overdue' : 'Trigger open',
    message: `${alert.text}${alert.due !== '—' ? ` · due ${alert.due}` : ''}`,
    timestamp: due.toISOString(),
    read: false,
    priority: alert.overdue ? 'high' : PRIORITY[alert.severity],
    actionUrl: `/pipeline?deal=${encodeURIComponent(alert.deal)}`,
    dealId: alert.deal,
    module: 'pipeline',
    icon: 'AlertTriangle',
  }
}

